<!-- 居中 HUD —— 在 App.vue 里挂载一次即可。替代 Element Plus 的顶部 ElMessage 提示。 -->
<template>
  <teleport to="body">
    <transition name="hud-scrim">
      <div v-if="items.length" class="hud-scrim"></div>
    </transition>
    <div class="hud-layer" aria-live="polite">
      <transition-group name="hud-pop" tag="div" class="hud-stack">
        <div
          v-for="it in items"
          :key="it.id"
          class="hud-card"
          :role="it.type === 'error' ? 'alert' : 'status'"
        >
          <span class="hud-icon" :style="{ background: meta(it).soft, color: meta(it).color }">
            <i :class="[meta(it).icon, { 'hud-spin': it.type === 'loading' }]"></i>
          </span>
          <span class="hud-text">{{ it.text }}</span>
          <button v-if="it.undo" class="hud-undo" type="button" @click="onUndo(it)">
            <i class="ri-arrow-go-back-line"></i>撤销
          </button>
        </div>
      </transition-group>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { hudState, close, type HudItem, type HudType } from '@/utils/hud';

const items = hudState.items;

const META: Record<HudType, { icon: string; color: string; soft: string }> = {
  success: { icon: 'ri-check-line',       color: 'var(--pp-success)', soft: 'var(--pp-success-soft)' },
  error:   { icon: 'ri-close-line',       color: 'var(--pp-danger)',  soft: 'var(--pp-danger-soft)' },
  warning: { icon: 'ri-alert-line',       color: 'var(--pp-warn)',    soft: 'var(--pp-warn-soft)' },
  info:    { icon: 'ri-information-line', color: 'var(--pp-info)',    soft: 'var(--pp-info-soft)' },
  loading: { icon: 'ri-loader-4-line',    color: 'var(--pp-accent)',  soft: 'var(--pp-accent-soft)' },
  undo:    { icon: 'ri-delete-bin-line',  color: '#56524b',           soft: 'var(--pp-divider)' },
};

const meta = (it: HudItem) => META[it.type];

function onUndo(it: HudItem) {
  it.undo?.();
  close(it.id);
}
</script>

<style scoped>
.hud-scrim {
  position: fixed;
  inset: 0;
  z-index: 3000;
  background: rgba(26, 25, 22, 0.12);
  pointer-events: none; /* 不拦截点击；若加载态想阻断交互可改 auto */
}
.hud-layer {
  position: fixed;
  inset: 0;
  z-index: 3001;
  display: grid;
  place-items: center;
  pointer-events: none;
}
.hud-stack {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}
.hud-card {
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  min-width: 138px;
  max-width: 240px;
  padding: 22px 26px;
  text-align: center;
  background: var(--pp-panel);
  border: 1px solid var(--pp-border);
  border-radius: 18px;
  box-shadow: 0 18px 46px rgba(26, 25, 22, 0.22);
}
.hud-icon {
  display: grid;
  place-items: center;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  font-size: 26px;
}
.hud-text {
  font-family: var(--pp-font-ui);
  font-size: 15px;
  font-weight: 600;
  line-height: 1.35;
  color: var(--pp-ink);
}
.hud-undo {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 1px;
  padding: 2px 6px;
  background: none;
  border: 0;
  cursor: pointer;
  font-family: var(--pp-font-ui);
  font-size: 13px;
  font-weight: 700;
  color: var(--pp-accent);
}
.hud-undo:hover { color: var(--pp-accent-dark); }
.hud-undo i { font-size: 14px; }
.hud-spin { display: inline-flex; animation: hud-spin 0.9s linear infinite; }
@keyframes hud-spin { to { transform: rotate(360deg); } }

/* 入场 / 出场 */
.hud-pop-enter-active { transition: opacity 0.36s cubic-bezier(0.22, 1, 0.36, 1), transform 0.36s cubic-bezier(0.22, 1, 0.36, 1); }
.hud-pop-leave-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.hud-pop-enter-from { opacity: 0; transform: scale(0.9); }
.hud-pop-leave-to { opacity: 0; transform: scale(0.96); }
.hud-scrim-enter-active, .hud-scrim-leave-active { transition: opacity 0.28s ease; }
.hud-scrim-enter-from, .hud-scrim-leave-to { opacity: 0; }

@media (prefers-reduced-motion: reduce) {
  .hud-pop-enter-active, .hud-pop-leave-active { transition: opacity 0.15s linear; }
  .hud-pop-enter-from, .hud-pop-leave-to { transform: none; }
  .hud-spin { animation: none; }
}
</style>
